import { Loader2 } from 'lucide-react';

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ className = '' }: { className?: string }) {
  return <Loader2 className={`animate-spin text-gray-400 ${className}`} />;
}

// ── Badge ─────────────────────────────────────────────────────────────────────
type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'mono';

const BADGE_STYLES: Record<BadgeVariant, string> = {
  default: 'bg-white/10 text-gray-300',
  success: 'bg-emerald-500/15 text-emerald-400',
  warning: 'bg-amber-500/15 text-amber-400',
  danger:  'bg-red-500/15 text-red-400',
  info:    'bg-primary/15 text-primary',
  mono:    'bg-white/[0.06] text-gray-400 font-mono text-[11px]',
};

export function Badge({
  children,
  variant = 'default',
}: {
  children: React.ReactNode;
  variant?: BadgeVariant;
}) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${BADGE_STYLES[variant]}`}>
      {children}
    </span>
  );
}

// ── PageHeader ────────────────────────────────────────────────────────────────
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between mb-5">
      <div>
        <h1 className="text-lg font-semibold text-white">{title}</h1>
        {subtitle && <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────
export function Table({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-bg-surface rounded-xl border border-white/[0.06] overflow-hidden">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  );
}

export function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider bg-white/[0.02] border-b border-white/[0.06] ${className}`}>
      {children}
    </th>
  );
}

export function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <td className={`px-4 py-2.5 text-gray-200 border-b border-white/[0.04] ${className}`}>
      {children}
    </td>
  );
}

export function TrEmpty({ cols, message = 'No data' }: { cols: number; message?: string }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-8 text-center text-sm text-gray-500">
        {message}
      </td>
    </tr>
  );
}

// ── Btn ───────────────────────────────────────────────────────────────────────
type BtnVariant = 'primary' | 'ghost' | 'danger';

const BTN_STYLES: Record<BtnVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary/90',
  ghost:   'bg-white/[0.06] text-gray-300 hover:bg-white/10 border border-white/[0.08]',
  danger:  'bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/20',
};

export function Btn({
  children,
  variant = 'ghost',
  disabled,
  onClick,
  type = 'button',
  className = '',
}: {
  children: React.ReactNode;
  variant?: BtnVariant;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  className?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${BTN_STYLES[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────
export function StatCard({
  label,
  value,
  sub,
  variant = 'default',
}: {
  label: string;
  value: string | number;
  sub?: string;
  variant?: 'default' | 'warning' | 'danger';
}) {
  const valueColor = variant === 'danger' ? 'text-red-400' : variant === 'warning' ? 'text-amber-400' : 'text-white';
  return (
    <div className="bg-bg-surface rounded-xl border border-white/[0.06] px-4 py-3">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${valueColor}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── LoadingState / ErrorState ─────────────────────────────────────────────────
export function LoadingState({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="flex items-center gap-2 py-12 justify-center text-gray-400 text-sm">
      <Spinner className="h-4 w-4" />
      {message}
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="py-12 text-center text-sm text-red-400">{message}</div>
  );
}
